import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHead, Section } from '../components/ui';

// Card text mirrors docs/academy-split/how-it-works.md (mentor + apprentice
// steps) — same words in the app, on rootegra.com, and here.

const JUSTIN = 'justincmeans@gmail.com';

export function Help() {
  const loc = useLocation();
  const pageUrl = typeof window !== 'undefined' ? window.location.origin + loc.pathname : loc.pathname;
  const subject = encodeURIComponent(`Backbar question — ${pageUrl}`);
  const body = encodeURIComponent(`Hi Justin,\n\nI was on ${pageUrl} and \n\n`);

  // Links like /help#set-up-threads land on that section.
  useEffect(() => {
    if (!loc.hash) return;
    document.getElementById(loc.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [loc.hash]);

  return (
    <>
      <PageHead
        title="Help"
        sub="How the Academy works and how to do the common jobs here. Anything not covered, send to Justin."
        right={
          <a className="lp-btn lp-btn--primary" href={`mailto:${JUSTIN}?subject=${subject}&body=${body}`}>
            Send to Justin
          </a>
        }
      />

      <Section title="How it works">
        <div className="lp-help-grid">
          <div className="lp-help-card">
            <h3>For mentors</h3>
            <ol>
              <li><strong>Create your account.</strong> Sign up with your email, name your salon, and enter its address. That address is where apprentice hours clock in. You can turn the address check off in Settings.</li>
              <li><strong>Invite your apprentice.</strong> Send the invite from Apprentices. They join with the link, and the two accounts are connected.</li>
              <li><strong>Confirm the state paperwork.</strong> The apprentice's first module is the state registration checklist. Your part of the list is on their roster card. Nothing counts until the permit or license exists.</li>
              <li><strong>Assign modules.</strong> Assign a module with a due date, or let the apprentice work through the curriculum in order. You see progress, quiz scores, and what they missed.</li>
              <li><strong>Approve hours.</strong> Hours clock in at the salon and wait for your approval. Edits come to you as requests.</li>
              <li><strong>Sign off practicals.</strong> When a skill is ready, watch it and sign it off in the app. Your sign-off is the only hands-on check the record carries.</li>
              <li><strong>Export the record.</strong> Hours, progress, and sign-offs export as a PDF in the layout your board expects, with the apprentice's permit or license number in the header.</li>
            </ol>
          </div>
          <div className="lp-help-card">
            <h3>For apprentices</h3>
            <ol>
              <li><strong>Join from your mentor's invite.</strong> Open the link, create your account, and you are connected to your salon.</li>
              <li><strong>Answer one question.</strong> Have you filed all of your state-required paperwork? If the answer is no or not sure, the Getting registered module is pinned to your Home until the paperwork is done.</li>
              <li><strong>Get registered.</strong> The module walks through your state's steps in order. Check off each item as you submit it; your mentor sees the same list.</li>
              <li><strong>Enter your permit or license number.</strong> When it arrives, tap License received and enter the number, the state, and the date. It prints on your hours tracker and on every hours export.</li>
              <li><strong>Learn.</strong> Work through the modules in order, or the ones your mentor assigns. Each lesson ends with a short quiz; each module ends with an exam.</li>
              <li><strong>Log hours.</strong> Clock in from the salon. Your mentor approves the hours, and you can request an edit if something is wrong.</li>
              <li><strong>Practice.</strong> The practice games are open any time and never count against you.</li>
              <li><strong>Get signed off.</strong> When a skill is ready, your mentor watches you do it and signs it off in the app.</li>
            </ol>
          </div>
        </div>
      </Section>

      <Section title="How to">
        <div className="lp-help-grid">
          <div className="lp-help-card">
            <h3>Reset someone's password</h3>
            <ol>
              <li>Open <Link to="/people">People</Link> and search their name or email.</li>
              <li>Tap the person, then <strong>Send a password reset</strong>. They get an email with a link that opens the app.</li>
              <li>If they cannot find the email, tap <strong>Set a temporary password</strong> instead. Read it to them over the phone, then close the box. They will be asked to choose a new one when they sign in.</li>
            </ol>
          </div>
          <div className="lp-help-card">
            <h3>Give free months</h3>
            <ol>
              <li>Open <Link to="/money">Money</Link> and tap <strong>Give free access</strong>.</li>
              <li>Pick the person, the plan, and how long. Write why in one line; it stays on the record.</li>
              <li>Free access shows on their card on People. Extend or end it from Money at any time.</li>
            </ol>
          </div>
          <div className="lp-help-card">
            <h3>Turn a game off</h3>
            <ol>
              <li>Open <Link to="/games">Games</Link>.</li>
              <li>Tap the switch next to the game. Off hides it from every apprentice within a minute.</li>
              <li>Nothing is deleted. Tap the switch again to bring it back.</li>
            </ol>
          </div>
          <div className="lp-help-card">
            <h3>How the month closes</h3>
            <ol>
              <li>Open <Link to="/books">Books</Link>. Pick the month.</li>
              <li><strong>What came in</strong>: pull the numbers from Apple, or type them in from the App Store Connect payment report.</li>
              <li><strong>What it cost</strong>: add each shared bill. Rootegra's share is filled in for you.</li>
              <li><strong>The split</strong>: check the numbers, then tap <strong>Close this month</strong>. Closing locks them. A closed month is corrected with an adjustment and a reason, never by editing.</li>
              <li><strong>Paid</strong>: Leah's share is sent as a member distribution. Record the payout by hand, or send it through Mercury and Justin approves it there.</li>
            </ol>
          </div>
          <div className="lp-help-card">
            <h3>Get a picture made</h3>
            <ol>
              <li>Open <Link to="/illustrations">Illustrations</Link>. Every lesson slot without a picture is listed.</li>
              <li>Copy the work order for the slot and paste it into ChatGPT. It has the house style built in.</li>
              <li>Drop the finished picture onto the slot. It is live in the lesson right away.</li>
            </ol>
          </div>
          <div className="lp-help-card">
            <h3>Reminders</h3>
            <p>Mentors get one email a day, at most, with what is waiting: hours to approve, practicals to schedule, an apprentice who has gone quiet, a module due soon, an hours milestone, a subscription ending.</p>
            <p>Apprentices are reminded if the paperwork checklist has sat untouched for 14 days, and again if the license number is still missing 30 days after the paperwork is done.</p>
          </div>
        </div>
      </Section>

      <div id="set-up-threads" style={{ scrollMarginTop: 72 }}>
        <Section title="Set up Threads">
          <div className="lp-help-grid">
            <div className="lp-help-card">
              <h3>Marketing on Threads</h3>
              <p>Leah's one-time setup: create the @rootegra Threads account, connect it, sign Claude Code in on the Mac mini. Full steps in the <a href="https://github.com/laurameans/rootegra-docs/blob/main/marketing-autopilot/LEAH-SETUP.md" target="_blank" rel="noreferrer">setup checklist</a> (on GitHub; if it asks you to sign in, ask Laura or Justin for access).</p>
              <p>Until then, posts wait in the <Link to="/marketing/queue">Marketing queue</Link> and nothing goes out. Once it is connected, approved posts go out on their own at their scheduled times, and replies show up in the <Link to="/marketing/inbox">Inbox</Link>.</p>
            </div>
          </div>
        </Section>
      </div>

      <Section title="State rules">
        <div className="lp-help-grid">
          <div className="lp-help-card">
            <h3>Hawaii</h3>
            <p>A supervisor holds a Hawaii license with at least one year of licensed experience and supervises one apprentice at a time. The permit is mailed to the shop.</p>
          </div>
          <div className="lp-help-card">
            <h3>California</h3>
            <p>A salon and a trainer alone cannot register an apprentice. The apprenticeship must run through a program sponsor approved by the Division of Apprenticeship Standards and the Board, and a trainer may have up to two apprentices. Find a sponsor first; the forms come from them.</p>
          </div>
        </div>
        <p className="lp-sub">For anything about a state's rules, the board is the source; Rootegra records what the board decides.</p>
      </Section>
    </>
  );
}
